import { FC } from 'react';
import { Eye, Home, Lock, X, HelpCircle } from 'react-feather';
import styled from 'styled-components';
import AccountImage from './AccountImage';
import { AuthConsumer } from '../../../containers/shared/auth/AuthContext';
import Can from '../../../containers/shared/auth/Can';
import { images, theme } from '../../../theme';
import { Logo, MobileAccountImageContainer } from './StyledElements';
import { NavItem } from '.';
import MobileSideBarLink from './MobileSideBarLink';
import { useRouter } from '../../../hooks';

type MobileSideBarProps = {
  context: any;
  isWeb?: boolean;
  navItems: Array<NavItem>;
  portalType: string;
};

type SideBarWrapperProps = {
  open: boolean;
  className?: string;
  children: React.ReactNode;
  onClick?: () => void;
};

type SideBarFooterProps = {
  open: boolean;
};

const SideBarWrapper = styled.div<SideBarWrapperProps>`
  -webkit-transform: ${props => (!props.open ? 'translate3d(-100%, 0px, 0px)' : 'translate3d(0px, 0px, 0px)')};
  transform: ${props => (!props.open ? 'translate3d(-100%, 0px, 0px)' : 'translate3d(0px, 0px, 0px)')};
  visibility: visible;
  transition: all 0.5s ease 0s;
  -webkit-transition: all 0.5s ease 0s;
  color: ${props => props.theme.colors.primary};
  z-index: 100;
  position: fixed;
  max-width: 100%;
  min-width: 260px;
  height: calc(100vh / var(--ib-ui-scale, 1));
  max-height: calc(100vh / var(--ib-ui-scale, 1));
  box-sizing: border-box;
  background: ${props => props.theme.colors.white};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  box-shadow: 1px 0px 5px 1px rgba(0, 0, 0, 0.15);
  & .sidebar-menu-icon {
    font-size: 21px;
    margin-left: 20px;
    transition: all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  }
  & .nav-link-text-hidden > div > span {
    opacity: 0;
    visibility: hidden;
    width: 0;
    transition: all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  }
`;

const SideBarFooter = styled.div<SideBarFooterProps>`
  bottom: 0;
  display: flex;
  justify-content: flex-end;
  padding: 0 20px 10px 20px;
  position: fixed;
  width: 100%;
  & ${Logo} {
    width: 100px;
    margin-right: 40px;
    margin-bottom: 10px;
    transition-property: visibility, opacity;
    opacity: ${props => (props.open ? 1 : 0)};
    transition: all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    visibility: ${props => (props.open ? 'visible' : 'hidden')};
  }
`;

const LogoutPairContainer = styled.div`
  bottom: 10px;
  display: inline-block;
  left: 20px;
  padding-bottom: 0;
  position: absolute;
`;

const LogoutButton = styled.div<any>`
  background: none;
  border: none;
  font-size: 13px;
  left: 20px;
  padding: 0;
  position: absolute;
  top: 6px;
`;

const MobileSideBarContent = styled.div`
  flex: 1;
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  min-height: 0;
  padding-bottom: 120px;
`;

const MobileSideBar: FC<MobileSideBarProps> = ({ context, isWeb, navItems }) => {
  const { match } = useRouter();
  return (
    <AuthConsumer>
      {({ user, logout, hasCaseManagement }) => {
        const userName = user.lastName || user.firstName;
        const isViewerRole = user.roles.some(role => role.includes('VIEWER'));
        const landingObject = isViewerRole
          ? { title: 'Insights', icon: <Eye size={20} className="sidebar-menu-icon" color={theme.colors.primary} /> }
          : { title: 'Dashboard', icon: <Home size={20} className="sidebar-menu-icon" color={theme.colors.primary} /> };

        return (
          <SideBarWrapper className="custom-sidebar" open={context.openMobileDrawer}>
            <MobileAccountImageContainer>
              <AccountImage profileURL={`/institution/my-profile`} userName={userName || 'User'} />
              <X onClick={context.toggleMobileDrawer} />
            </MobileAccountImageContainer>

            <MobileSideBarContent>
              {navItems &&
                navItems.length > 0 &&
                navItems.map(i => (
                  <Can
                    landingPage={false}
                    key={i.to}
                    roles={user.roles}
                    plan={user.plan}
                    userAddOns={user.addOns}
                    action={`${i.to}:visit`}
                    navItemCheck={i.check ? i.check(user, hasCaseManagement) : undefined}
                    yes={() => (
                      <MobileSideBarLink
                        to={i.to}
                        isWeb={isWeb}
                        key={isViewerRole && i.title === 'Dashboard' ? landingObject.title : i.title}
                        icon={isViewerRole && i.title === 'Dashboard' ? landingObject.icon : i.icon}
                        title={isViewerRole && i.title === 'Dashboard' ? landingObject.title : i.title}
                        subMenu={i.subMenu}
                        activePathPrefix={i.activePathPrefix}
                        color={theme.colors.primary}
                        open={context.openMobileDrawer}
                        onClick={context.toggleMobileDrawer}
                      />
                    )}
                    no={() => null}
                  />
                ))}
              <MobileSideBarLink
                to={`${match.url}`}
                isWeb={isWeb}
                icon={<HelpCircle size={20} className="sidebar-menu-icon" color={theme.colors.primary} />}
                title={'Support'}
                color={theme.colors.primary}
                open={context.openMobileDrawer}
                onClick={() => {
                  // @ts-ignore
                  window.FreshworksWidget('open');
                }}
              />
            </MobileSideBarContent>

            <SideBarFooter open={context.openMobileDrawer}>
              <LogoutPairContainer>
                <Lock width="16px" />
                <LogoutButton onClick={logout}>Logout</LogoutButton>
              </LogoutPairContainer>
              <Logo open={context.drawerOpen} src={images.impactBoardLogo} />
            </SideBarFooter>
          </SideBarWrapper>
        );
      }}
    </AuthConsumer>
  );
};

export default MobileSideBar;
